import { oc, eventIterator } from "@orpc/contract";
import { z } from "zod";

/**
 * Module Key Schema - modules the chat agent can operate in
 */
export const ModuleKeySchema = z.enum([
  "home",
  "workflows",
  "teams",
  "query-builder",
  "template-blocks",
  "email-templates",
  "pdf-templates",
  "internal-ui-templates",
  "brand-kit",
  "rebates",
  // AR Vision Demo (GEN-2247) — catch-all module the scripted Nami pages
  // register under. Remove with frontend/src/demo/ar.
  "general",
]);

/**
 * Page Key Schema - finer-grained pages within a module that contribute
 * page-specific prompt fragments and tools. The backend page registry must
 * have an entry for every value here (enforced via Record<PageKey, ...>).
 */
export const PageKeySchema = z.enum(["workflow-editor", "workflow-detail"]);

/**
 * Structured content part for user messages that include image attachments.
 */
const MessageContentPartSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string() }),
  z.object({
    type: z.literal("image"),
    imageData: z.string().min(1),
    mimeType: z.enum(["image/jpeg", "image/png"]),
  }),
]);

/**
 * Message Attachment Schema - file metadata attached to a chat message
 */
export const MessageAttachmentSchema = z.object({
  documentId: z.string().uuid(),
  fileName: z.string(),
  mimeType: z.string().nullable(),
  fileSizeBytes: z.number().nullable(),
});

/**
 * Message Schema - conversation messages matching AI SDK pattern.
 * User messages may include a content array when an annotated screenshot
 * is attached (text part + image part).
 */
const MessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.union([z.string(), z.array(MessageContentPartSchema)]),
  attachments: z.array(MessageAttachmentSchema).optional(),
});

/**
 * Frontend tool definition — schema sent from the frontend so the AI model
 * can call tools that execute client-side (navigation, mutations via RPC
 * hooks, etc.). The backend registers these as passthrough tools whose
 * execute returns the input args unchanged; the frontend intercepts the
 * tool-result event and runs the real handler.
 *
 * When `awaitable` is true the tool is promise-backed: the backend blocks the
 * tool's execute() until the frontend POSTs a result via submitFrontendToolResult,
 * so the agent sees the frontend-computed result in the same turn. The frontend
 * runs an awaitable tool on the `tool-call` event (not `tool-result`, which the
 * backend won't emit until the bridge resolves).
 */
export const FrontendToolSchema = z.object({
  name: z.string().min(1),
  description: z.string().min(1),
  parameters: z.record(z.string(), z.unknown()),
  awaitable: z.boolean().optional(),
});
export type FrontendTool = z.infer<typeof FrontendToolSchema>;

/**
 * Chat Agent Chat Input Schema
 */
export const ChatAgentChatInputSchema = z.object({
  moduleKey: ModuleKeySchema,
  /**
   * Optional page-level identifier. Resolved server-side against the page
   * registry to layer page-specific prompt fragments and tools on top of
   * module defaults.
   */
  pageKey: PageKeySchema.optional(),
  currentPage: z.string(),
  messages: z.array(MessageSchema),
  threadId: z.string().uuid().optional(),
  moduleContext: z.record(z.string(), z.unknown()).optional(),
  frontendTools: z.array(FrontendToolSchema).optional(),
});

/**
 * Finish Reason Schema - reasons why the stream ended
 */
export const FinishReasonSchema = z.enum([
  "stop",
  "length",
  "tool-calls",
  "content-filter",
  "error",
  "other",
]);

/**
 * Agent Stream Event Schema - discriminated union with 6 event types
 */
export const AgentStreamEventSchema = z.discriminatedUnion("type", [
  // Text delta event - streamed text chunks
  z.object({
    type: z.literal("text-delta"),
    text: z.string(),
  }),
  // Tool call event - when agent invokes a tool
  z.object({
    type: z.literal("tool-call"),
    toolCallId: z.string(),
    toolName: z.string(),
    args: z.record(z.string(), z.unknown()),
    userFacingName: z.string().optional(),
  }),
  // Tool result event - result from tool execution
  z.object({
    type: z.literal("tool-result"),
    toolCallId: z.string(),
    toolName: z.string(),
    result: z.unknown(),
  }),
  // Thread info event - thread metadata
  z.object({
    type: z.literal("thread-info"),
    threadId: z.string().uuid(),
    isNew: z.boolean(),
  }),
  // Finish event - stream completed
  z.object({
    type: z.literal("finish"),
    finishReason: FinishReasonSchema,
  }),
  // Assistant message persisted to DB - emitted after `finish` once the
  // assistant turn has been written to `ai_thread_messages`. Carries the
  // stable DB id (so the frontend can attach durable per-message feedback),
  // the Langfuse traceId for this turn, and the spanId of the AI SDK's
  // outermost `ai.streamText` observation (so user-feedback scores attach
  // to that specific observation rather than to the trace as a whole).
  z.object({
    type: z.literal("assistant-message-saved"),
    messageId: z.string().uuid(),
    traceId: z.string().nullable(),
    observationId: z.string().nullable(),
  }),
  // Draft snapshot event - progressive workflow draft state emitted after
  // each draft mutation tool completes, so the frontend can render the
  // workflow being built in real time.
  z.object({
    type: z.literal("draft-snapshot"),
    toolCallId: z.string(),
    draft: z.unknown(),
  }),
  // Tool input streaming event - emitted (throttled) while the model is
  // still generating a tool call's JSON input, before the `tool-call`
  // event fires. Carries the accumulated partial input text so the
  // frontend can progressively parse and render an incomplete spec (e.g.
  // streaming an email-template proposal into the editor/preview as it is
  // written). The final `tool-call`/`tool-result` remains the source of
  // truth and overwrites whatever partial state was applied.
  z.object({
    type: z.literal("tool-input-delta"),
    toolCallId: z.string(),
    toolName: z.string(),
    /** Accumulated partial JSON text of the tool input so far. */
    partialInput: z.string(),
  }),
  // Error event - something went wrong
  z.object({
    type: z.literal("error"),
    message: z.string(),
    code: z.string().optional(),
  }),
]);

/**
 * Yield Decision Schemas - telemetry for the generic "agent yields to
 * user" event. The first concrete consumer is the workflow-proposal
 * Apply Changes card; future yield kinds (approvals, clarifying
 * questions) reuse the same endpoint so yield-acceptance rate is a single
 * queryable span.
 */
export const YieldDecisionSchema = z.enum(["accepted", "discarded"]);

export const RecordYieldDecisionInputSchema = z.object({
  yieldId: z.string().min(1),
  toolCallId: z.string().min(1),
  toolName: z.string().min(1),
  decision: YieldDecisionSchema,
  threadId: z.string().uuid().optional(),
  /** Page that produced this yield, for "acceptance rate by page" queries. */
  pageKey: PageKeySchema.optional(),
});

export const RecordYieldDecisionOutputSchema = z.object({
  ok: z.literal(true),
});

/**
 * Page element captured alongside a screenshot — gives the agent numeric IDs
 * it can pass directly to annotate_screen. Bounding rects are stored client-side.
 */
export const PageElementSchema = z.object({
  id: z.number().int().positive(),
  text: z.string(),
  role: z.string(),
  tag: z.string(),
  location: z.string(),
});

/**
 * Submit Screenshot Schemas - frontend delivers a captured screenshot
 * to unblock the server-side capture_screen tool's Promise bridge.
 */
export const SubmitScreenshotInputSchema = z.object({
  toolCallId: z.string().min(1),
  imageData: z.string().min(1),
  mimeType: z.enum(["image/jpeg", "image/png"]),
  elements: z.array(PageElementSchema).optional(),
});

export const SubmitScreenshotOutputSchema = z.object({
  ok: z.literal(true),
});

/**
 * Submit Frontend Tool Result Schemas - generic round-trip for promise-backed
 * (awaitable) frontend tools. The frontend POSTs the handler's return value to
 * unblock the server-side passthrough tool's Promise bridge, so the agent sees
 * the result in the same turn. `result` is whatever the frontend handler returns
 * (e.g. a socket diff for a dynamic workflow-step edit).
 */
export const SubmitFrontendToolResultInputSchema = z.object({
  toolCallId: z.string().min(1),
  result: z.unknown(),
});

export const SubmitFrontendToolResultOutputSchema = z.object({
  ok: z.literal(true),
});

/**
 * Chat Agent Contracts
 */
export const chatAgentContracts = {
  chat: oc
    .input(ChatAgentChatInputSchema)
    .output(eventIterator(AgentStreamEventSchema))
    .route({
      method: "POST",
      path: "/chat-agent/chat",
    }),
  recordYieldDecision: oc
    .input(RecordYieldDecisionInputSchema)
    .output(RecordYieldDecisionOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/yield-decision",
    }),
  submitScreenshot: oc
    .input(SubmitScreenshotInputSchema)
    .output(SubmitScreenshotOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/submit-screenshot",
    }),
  submitFrontendToolResult: oc
    .input(SubmitFrontendToolResultInputSchema)
    .output(SubmitFrontendToolResultOutputSchema)
    .route({
      method: "POST",
      path: "/chat-agent/submit-frontend-tool-result",
    }),
};

// Export types
export type MessageAttachment = z.infer<typeof MessageAttachmentSchema>;
export type ModuleKey = z.infer<typeof ModuleKeySchema>;
export type PageKey = z.infer<typeof PageKeySchema>;
export type MessageContentPart = z.infer<typeof MessageContentPartSchema>;
export type ChatAgentChatInput = z.infer<typeof ChatAgentChatInputSchema>;
export type FinishReason = z.infer<typeof FinishReasonSchema>;
export type AgentStreamEvent = z.infer<typeof AgentStreamEventSchema>;
export type YieldDecision = z.infer<typeof YieldDecisionSchema>;
export type RecordYieldDecisionInput = z.infer<
  typeof RecordYieldDecisionInputSchema
>;
export type RecordYieldDecisionOutput = z.infer<
  typeof RecordYieldDecisionOutputSchema
>;
export type PageElement = z.infer<typeof PageElementSchema>;
export type SubmitScreenshotInput = z.infer<typeof SubmitScreenshotInputSchema>;
export type SubmitScreenshotOutput = z.infer<
  typeof SubmitScreenshotOutputSchema
>;
export type SubmitFrontendToolResultInput = z.infer<
  typeof SubmitFrontendToolResultInputSchema
>;
export type SubmitFrontendToolResultOutput = z.infer<
  typeof SubmitFrontendToolResultOutputSchema
>;
